import React, { useContext, useMemo } from 'react';
import { useField } from 'react-final-form';
import styled, { css, useTheme } from 'styled-components';
import { AttentionBox, SelectField } from '@ubeya/shared-web/components';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useTags from '@ubeya/shared/hooks/account/useTags';
import { TagContainer, TagDot } from 'scheduling/components/ListView/TagsBadges';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 16px;

  ${({ $isInherited }) =>
    $isInherited &&
    css`
      .select-chip {
        pointer-events: none;

        svg {
          display: none;
        }
      }
    `}
`;

const InheritedChipLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.black};
`;

const EventSuffix = styled.span`
  color: ${({ theme }) => theme.colors.grey4};
`;

const InfoBanner = styled(AttentionBox)`
  padding: 12px;
  font-size: 13rem;
  align-items: center;
`;

const OverrideNote = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  font-size: 13rem;
  color: ${({ theme }) => theme.colors.grey4};
  margin-bottom: 12px;
`;

const StruckTag = styled(TagContainer)`
  color: ${({ theme }) => theme.colors.grey4};
  text-decoration: line-through;
`;

const INHERITED_CHIP_PREFIX = 'event:';

const ShiftTagsField = ({ eventTags = [] }) => {
  const { t } = useContext(ConfigContext);
  const theme = useTheme();
  const {
    input: { value: shiftTags = [] }
  } = useField('shiftTags');

  const { tagsOptions: options, mappedTags } = useTags();

  const eventTagOptions = useMemo(() => eventTags.map((tagId) => mappedTags[tagId]).filter(Boolean), [
    eventTags,
    mappedTags
  ]);
  const hasShiftTags = shiftTags?.length > 0;
  const isInherited = !hasShiftTags && eventTagOptions.length > 0;
  // Inherited chips use their own keys, so the real tags stay selectable in the dropdown
  // (the select hides options that are part of its value).
  const inheritedChipOptions = useMemo(
    () =>
      eventTagOptions.map(({ value, label, color }) => ({
        value: `${INHERITED_CHIP_PREFIX}${value}`,
        color: theme.colors.grey1,
        label: (
          <InheritedChipLabel>
            <TagDot $color={color} />
            {label}
            <EventSuffix>{t('eventTagSuffix')}</EventSuffix>
          </InheritedChipLabel>
        )
      })),
    [eventTagOptions, theme, t]
  );

  const fieldOptions = useMemo(() => (isInherited ? [...options, ...inheritedChipOptions] : options), [
    isInherited,
    options,
    inheritedChipOptions
  ]);

  const handleChange = (newValue, input) =>
    input.onChange(isInherited ? newValue.filter((id) => !String(id).startsWith(INHERITED_CHIP_PREFIX)) : newValue);

  return (
    <Wrapper $isInherited={isInherited}>
      <SelectField
        name="shiftTags"
        title={t('shiftTags')}
        options={fieldOptions}
        {...(isInherited && { value: inheritedChipOptions.map(({ value }) => value) })}
        onChange={handleChange}
        usePortal
      />

      {isInherited && (
        <InfoBanner type="info">
          {eventTagOptions.length === 1
            ? t('shiftInheritsEventTag', { name: eventTagOptions[0].label })
            : t('shiftInheritsEventTags', { value: eventTagOptions.length })}
        </InfoBanner>
      )}

      {hasShiftTags && eventTagOptions.length > 0 && (
        <OverrideNote>
          {t('shiftTagsReplaceEventTags')}
          {eventTagOptions.map(({ value, label }) => (
            <StruckTag key={value} $isFixedWidth>
              {label}
            </StruckTag>
          ))}
          {t('overridesEventTagsNote')}
        </OverrideNote>
      )}
    </Wrapper>
  );
};

export default ShiftTagsField;
